import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Search,
  Download,
  Upload,
  Eye,
  Trash2,
  CheckCircle,
  AlertCircle,
  Clock,
  Euro,
  DollarSign,
  PoundSterling,
  Coins,
  Calendar,
  User,
  X,
  Send,
  Edit
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddContractModal from '../Modals/AddContractModal';

interface Contract {
  id: string;
  contract_number: string;
  student_id: string;
  proposal_id: string;
  branch_id: string;
  created_by: string | null;
  contract_date: string;
  total_amount: number;
  currency: string;
  contract_file_url: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  transferred_to_operations: boolean;
  transferred_to_operations_at: string | null;
  transferred_by: string | null;
  students: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  branches: {
    name: string;
  } | null;
  creator: {
    full_name: string;
  } | null;
  lead_info?: {
    interested_programs: string[];
    price_notes: string | null;
  } | null;
}

const Contracts: React.FC = () => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, { status?: string; total_amount?: number }>>({});
  const [savingContractId, setSavingContractId] = useState<string | null>(null);
  const [userBranchName, setUserBranchName] = useState<string>('');

  const [showFixStudentModal, setShowFixStudentModal] = useState(false);
  const [contractToFix, setContractToFix] = useState<Contract | null>(null);
  const [fixStudentForm, setFixStudentForm] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [fixingStudent, setFixingStudent] = useState(false);

  useEffect(() => {
    if (user?.branchId) {
      supabase
        .from('branches')
        .select('name')
        .eq('id', user.branchId)
        .single()
        .then(({ data }) => {
          if (data) setUserBranchName(data.name);
        });
    }
  }, [user]);

  const handleInputChange = (contractId: string, field: 'status' | 'total_amount', value: any) => {
    setEditedValues(prev => ({
      ...prev,
      [contractId]: {
        ...prev[contractId],
        [field]: value
      }
    }));
  };

  const handleSaveChanges = async (contractId: string) => {
    const changes = editedValues[contractId];
    if (!changes || savingContractId === contractId) return;

    setSavingContractId(contractId);
    try {
      // PostgREST may return no error when RLS filters every row out of an
      // UPDATE. Read the changed row back so a no-op cannot look successful.
      const { data: updatedContract, error } = await supabase
        .from('contracts')
        .update(changes)
        .eq('id', contractId)
        .select('id, status, total_amount')
        .maybeSingle();

      if (error) throw error;
      if (!updatedContract) {
        throw new Error('Sözleşme güncellenemedi. Muhasebe güncelleme yetkisini kontrol edin.');
      }

      // STATUS CHANGE LOGIC
      // If status is changed to 'completed' -> Mark all installments as PAID
      // If status is changed to anything else (pending/active) -> Mark all installments as PENDING (revert payment)

      if (changes.status) {
        if (changes.status === 'completed') {
          // Attempt to update existing installments to paid
          const { data: updatedRows, error: updateError } = await supabase
            .from('installments')
            .update({
              status: 'paid',
              payment_date: new Date().toISOString()
            })
            .eq('contract_id', contractId)
            .select();

          if (updateError) {
            console.error("Error updating installments to paid:", updateError);
            // Verify if it's a permission issue? Continue anyway or throw?
          }

          // If no installments existed/updated, create one
          if (!updatedRows || updatedRows.length === 0) {
            const contract = contracts.find(c => c.id === contractId);
            if (contract) {
              const { error: insertError } = await supabase
                .from('installments')
                .insert({
                  contract_id: contractId,
                  student_id: contract.student_id,
                  branch_id: contract.branch_id,
                  installment_number: 1,
                  amount: contract.total_amount,
                  currency: contract.currency,
                  due_date: new Date().toISOString(),
                  payment_date: new Date().toISOString(),
                  status: 'paid',
                  notes: 'Sözleşme tamamlandı olarak işaretlendiği için otomatik oluşturuldu.'
                });

              if (insertError) console.error("Error creating auto-installment:", insertError);
            }
          }
        } else {
          // REVERT TO PENDING/CANCELLED
          const targetStatus = changes.status === 'cancelled' ? 'cancelled' : 'pending';

          const { error: revertError } = await supabase
            .from('installments')
            .update({
              status: targetStatus,
              payment_date: null
            })
            .eq('contract_id', contractId);

          if (revertError) console.error("Error reverting installments:", revertError);
        }
      }

      alert('Değişiklikler başarıyla kaydedildi');

      setContracts(prev => prev.map(c =>
        c.id === contractId ? { ...c, ...updatedContract } : c
      ));

      setEditedValues(prev => {
        const next = { ...prev };
        delete next[contractId];
        return next;
      });

    } catch (err) {
      console.error('Error updating contract:', err);
      const message = err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu.';
      alert(`Güncelleme sırasında hata oluştu: ${message}`);
    } finally {
      setSavingContractId(null);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [statusFilter]);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      console.log('Fetching contracts (Manual Join Strategy)...');
      console.log('Current User:', user);

      // 1. Fetch Raw Contracts
      let query = supabase
        .from('contracts')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (user?.role?.toLowerCase() !== 'admin' &&
        user?.role?.toLowerCase() !== 'super_admin' &&
        user?.unitType?.toLowerCase() !== 'accounting' &&
        user?.unitType?.toLowerCase() !== 'operations') {
        if (user?.branchId) {
          query = query.eq('branch_id', user.branchId);
        }
      }

      const { data: rawContracts, error: contractsError } = await query;
      if (contractsError) throw contractsError;

      if (!rawContracts || rawContracts.length === 0) {
        setContracts([]);
        setLoading(false);
        return;
      }

      // 2. Fetch Relations manually because PostgREST crashes on NOT VALID constraints
      const studentIds = [...new Set(rawContracts.map(c => c.student_id).filter(Boolean))];
      const branchIds = [...new Set(rawContracts.map(c => c.branch_id).filter(Boolean))];
      const creatorIds = [...new Set(rawContracts.map(c => c.created_by).filter(Boolean))];

      const [studentsRes, branchesRes, profilesRes, leadsRes] = await Promise.all([
        studentIds.length > 0 ? supabase.from('students').select('id, first_name, last_name').in('id', studentIds) : Promise.resolve({ data: [] }),
        branchIds.length > 0 ? supabase.from('branches').select('id, name').in('id', branchIds) : Promise.resolve({ data: [] }),
        creatorIds.length > 0 ? supabase.from('profiles').select('id, full_name').in('id', creatorIds) : Promise.resolve({ data: [] }),
        studentIds.length > 0 ? supabase.from('leads').select('id, student_id, first_name, last_name, interested_programs, price_notes').or(`id.in.(${studentIds.join(',')}),student_id.in.(${studentIds.join(',')})`) : Promise.resolve({ data: [] })
      ]);

      const studentsMap = new Map((studentsRes.data || []).map((s: any) => [s.id, s]));
      const branchesMap = new Map((branchesRes.data || []).map((b: any) => [b.id, b]));
      const profilesMap = new Map((profilesRes.data || []).map((p: any) => [p.id, p]));
      const leadsMap = new Map();
      (leadsRes.data || []).forEach((l: any) => {
        if (l.student_id) leadsMap.set(l.student_id, l);
        leadsMap.set(l.id, l);
      });

      const enrichedContracts = rawContracts.map(contract => {
        const studentData = contract.student_id ? studentsMap.get(contract.student_id) : null;
        const leadData = contract.student_id ? leadsMap.get(contract.student_id) : null;

        return {
          ...contract,
          students: studentData || (leadData ? {
            id: contract.student_id!,
            first_name: leadData.first_name,
            last_name: leadData.last_name
          } : null),
          branches: contract.branch_id ? branchesMap.get(contract.branch_id) || null : null,
          creator: contract.created_by ? profilesMap.get(contract.created_by) || null : null,
          lead_info: leadData || null
        };
      });

      console.log('Enriched Contracts:', enrichedContracts);
      setContracts(enrichedContracts as any);

    } catch (err) {
      console.error('Error fetching contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (contractId: string, file: File) => {
    setUploadingFile(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${contractId}-${Date.now()}.${fileExt}`;
      const filePath = `contracts/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('contracts')
        .update({ contract_file_url: publicUrl })
        .eq('id', contractId);

      if (updateError) throw updateError;

      alert('Sözleşme dosyası başarıyla yüklendi');
      fetchContracts();
      setShowUploadModal(false);
      setSelectedContract(null);
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Dosya yüklenirken hata oluştu');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteContract = async (contractId: string) => {
    if (!await window.appConfirm('Bu sözleşmeyi silmek istediğinizden emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('contracts')
        .delete()
        .eq('id', contractId);

      if (error) throw error;

      alert('Sözleşme başarıyla silindi');
      fetchContracts();
    } catch (err) {
      console.error('Error deleting contract:', err);
      alert('Sözleşme silinirken hata oluştu');
    }
  };

  const handleTransferToOperations = async (contract: Contract) => {
    if (!await window.appConfirm(`${contract.students?.first_name} ${contract.students?.last_name} adlı öğrenciyi operasyon ekibine göndermek istediğinizden emin misiniz?`)) return;

    try {
      const { error } = await supabase
        .from('contracts')
        .update({
          transferred_to_operations: true,
          transferred_to_operations_at: new Date().toISOString(),
          transferred_by: user!.id
        })
        .eq('id', contract.id);

      if (error) throw error;

      alert('Öğrenci başarıyla operasyon ekibine gönderildi!');
      fetchContracts();
    } catch (err) {
      console.error('Error transferring to operations:', err);
      alert('Operasyona gönderme işlemi sırasında hata oluştu');
    }
  };

  const handleSendToAccounting = async (contract: Contract) => {
    if (!await window.appConfirm(`${contract.students?.first_name} ${contract.students?.last_name} adlı öğrencinin sözleşmesi muhasebe onayına gönderilsin mi?`)) return;

    try {
      const { error } = await supabase
        .from('contracts')
        .update({
          notes: (contract.notes ? contract.notes + '\n' : '') + `[${new Date().toLocaleDateString()}] Satış tarafından muhasebe onayına sunuldu.`
        })
        .eq('id', contract.id);

      if (error) throw error;

      alert('Sözleşme başarıyla muhasebeye iletildi!');
      fetchContracts();
    } catch (err) {
      console.error('Error sending to accounting:', err);
      alert('Muhasebeye gönderme işlemi sırasında hata oluştu');
    }
  };

  const handleFixStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contractToFix) return;

    if (!fixStudentForm.first_name || !fixStudentForm.last_name) {
      alert('Ad ve soyad zorunludur.');
      return;
    }

    setFixingStudent(true);
    try {
      // 1. Yeni Öğrenci oluştur
      const { data: newStudent, error: studentError } = await supabase
        .from('students')
        .insert({
          first_name: fixStudentForm.first_name,
          last_name: fixStudentForm.last_name,
          phone: fixStudentForm.phone || null,
          email: fixStudentForm.email || null,
          branch_id: contractToFix.branch_id,
          created_by: user!.id
        })
        .select()
        .single();

      if (studentError) throw studentError;

      // 2. Sözleşmeyi güncelle
      const { error: contractError } = await supabase
        .from('contracts')
        .update({ student_id: newStudent.id })
        .eq('id', contractToFix.id);

      if (contractError) throw contractError;

      alert('Öğrenci başarıyla oluşturuldu ve sözleşmeye bağlandı.');
      setShowFixStudentModal(false);
      setContractToFix(null);
      setFixStudentForm({ first_name: '', last_name: '', phone: '', email: '' });
      fetchContracts(); // Yenile
    } catch (err) {
      console.error('Error fixing student:', err);
      alert('Öğrenci düzeltilirken hata oluştu');
    } finally {
      setFixingStudent(false);
    }
  };

  const getCurrencyIcon = (currency: string, className: string = "w-5 h-5 text-green-600 dark:text-green-400") => {
    switch (currency?.toUpperCase()) {
      case 'EUR': return <Euro className={className} />;
      case 'USD': return <DollarSign className={className} />;
      case 'GBP': return <PoundSterling className={className} />;
      case 'TRY': return <span className={`font-bold text-green-600 dark:text-green-400 leading-none ${className === "w-5 h-5 text-green-600 dark:text-green-400" ? "text-lg" : "text-xs"}`}>₺</span>;
      default: return <Coins className={className} />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
      active: { label: 'Aktif', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400', icon: CheckCircle },
      completed: { label: 'Tamamlandı', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400', icon: CheckCircle },
      cancelled: { label: 'İptal', color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', icon: AlertCircle },
      pending: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400', icon: Clock }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const filteredContracts = contracts.filter(contract => {
    const searchLower = searchTerm.toLowerCase();
    return (
      contract.contract_number.toLowerCase().includes(searchLower) ||
      (contract.students?.first_name?.toLowerCase().includes(searchLower)) ||
      (contract.students?.last_name?.toLowerCase().includes(searchLower))
    );
  });

  const contractsByBranch = filteredContracts.reduce((acc, contract) => {
    const branchName = contract.branches?.name || 'Şube Belirtilmemiş';
    if (!acc[branchName]) {
      acc[branchName] = [];
    }
    acc[branchName].push(contract);
    return acc;
  }, {} as Record<string, Contract[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Sözleşmeler</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Tüm öğrenci sözleşmelerini görüntüleyin ve yönetin
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          <Plus className="w-5 h-5" />
          Yeni Sözleşme
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Sözleşme numarası veya öğrenci adı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="completed">Tamamlandı</option>
          <option value="cancelled">İptal</option>
          <option value="pending">Beklemede</option>
        </select>
      </div>

      {filteredContracts.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Sözleşme bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' ? 'Arama kriterlerinize uygun sözleşme bulunamadı.' : 'Henüz sözleşme eklenmemiş.'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(contractsByBranch).map(([branchName, branchContracts]) => (
            <div key={branchName} className="space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b-2 border-blue-500 dark:border-blue-400">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{branchName}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {branchContracts.length} sözleşme
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {branchContracts.map((contract) => (
                  <div
                    key={contract.id}
                    className="bg-white dark:bg-gray-900 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-8 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-600 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-6">
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {contract.students ? `${contract.students.first_name} ${contract.students.last_name}` : 'Bilinmeyen Öğrenci'}
                              </h3>
                              {!contract.students && (
                                <button
                                  onClick={() => {
                                    setContractToFix(contract);
                                    setFixStudentForm({ first_name: '', last_name: '', phone: '', email: '' });
                                    setShowFixStudentModal(true);
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60 transition-colors"
                                  title="Eksik öğrenci bilgisini tamamla"
                                >
                                  <Edit className="w-3 h-3" />
                                  Düzelt
                                </button>
                              )}
                            </div>
                            <p className="text-base text-gray-500 dark:text-gray-400 font-medium">
                              {contract.contract_number}
                            </p>
                          </div>
                          {getStatusBadge(contract.status)}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Sözleşme Tarihi</p>
                              <p className="text-base font-semibold text-gray-900 dark:text-white">
                                {format(new Date(contract.contract_date), 'dd MMM yyyy', { locale: tr })}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center w-9 h-9">
                              {getCurrencyIcon(contract.currency)}
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Toplam Tutar</p>
                              {(user?.role === 'admin' || user?.unitType === 'accounting') ? (
                                <input
                                  type="number"
                                  value={editedValues[contract.id]?.total_amount ?? contract.total_amount}
                                  onChange={(e) => handleInputChange(contract.id, 'total_amount', parseFloat(e.target.value))}
                                  className="text-lg font-bold text-gray-900 dark:text-white bg-transparent border-b border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:outline-none w-32"
                                />
                              ) : (
                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                  {contract.currency} {contract.total_amount.toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                              <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Şube</p>
                              <p className="text-base font-semibold text-gray-900 dark:text-white">
                                {contract.branches?.name || 'Şube bilgisi yok'}
                              </p>
                            </div>
                          </div>

                          {contract.creator && (
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                <User className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Olusturan</p>
                                <p className="text-base font-semibold text-gray-900 dark:text-white">
                                  {contract.creator.full_name}
                                </p>
                              </div>
                            </div>
                          )}

                          {contract.lead_info?.interested_programs && contract.lead_info.interested_programs.length > 0 && (
                            <div className="flex items-center gap-3 md:col-span-2">
                              <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                                <FileText className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Program</p>
                                <p className="text-base font-semibold text-gray-900 dark:text-white">
                                  {contract.lead_info.interested_programs.join(', ')}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Status Editing for Admin/Accounting/Non-Central Sales */}
                          {(user?.role === 'admin' || user?.unitType === 'accounting' || (user?.unitType === 'sales' && !userBranchName?.toLowerCase().includes('merkez'))) && (
                            <div className="flex items-center gap-3 md:col-span-2">
                              <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Durum Düzenle</p>
                                <div className="flex items-center gap-2">
                                  <select
                                    value={editedValues[contract.id]?.status ?? contract.status}
                                    onChange={(e) => handleInputChange(contract.id, 'status', e.target.value)}
                                    className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  >
                                    <option value="pending">Beklemede</option>
                                    <option value="active">Onaylandı</option>
                                    <option value="completed">Ödeme Alındı</option>
                                    <option value="cancelled">Reddedildi</option>
                                  </select>

                                  {editedValues[contract.id] && (
                                    <button
                                      onClick={() => handleSaveChanges(contract.id)}
                                      disabled={savingContractId === contract.id}
                                      className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                      {savingContractId === contract.id ? 'Kaydediliyor...' : 'Kaydet'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {contract.notes && (
                          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Notlar</p>
                            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                              {contract.notes}
                            </p>
                          </div>
                        )}

                        {contract.lead_info?.price_notes && (
                          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                            <p className="text-xs text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-1 font-semibold">Ucret Ek Bilgi</p>
                            <p className="text-sm text-amber-900 dark:text-amber-300">
                              {contract.lead_info.price_notes}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => {
                              setSelectedContract(contract);
                              setShowDetailModal(true);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Detay
                          </button>

                          {contract.contract_file_url ? (
                            <>
                              <a
                                href={contract.contract_file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                              >
                                <Download className="w-4 h-4" />
                                Sözleşmeyi İndir
                              </a>
                              <button
                                onClick={() => {
                                  setSelectedContract(contract);
                                  setShowUploadModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <Upload className="w-4 h-4" />
                                Yeniden Yükle
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedContract(contract);
                                setShowUploadModal(true);
                              }}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                            >
                              <Upload className="w-4 h-4" />
                              Sözleşme Yükle
                            </button>
                          )}

                          {(user?.unitType === 'accounting' || user?.role === 'admin' || user?.unitType === 'sales') && !contract.transferred_to_operations && (
                            <button
                              onClick={() => handleTransferToOperations(contract)}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                            >
                              <Send className="w-4 h-4" />
                              Operasyona Gönder
                            </button>
                          )}

                          {(user?.unitType === 'sales' && userBranchName?.toLowerCase().includes('merkez')) && !contract.transferred_to_operations && (
                            <button
                              onClick={() => handleSendToAccounting(contract)}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                            >
                              <Send className="w-4 h-4" />
                              Muhasebeye Gönder
                            </button>
                          )}

                          {contract.transferred_to_operations && (
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                              <CheckCircle className="w-4 h-4" />
                              Operasyona Gönderildi
                            </div>
                          )}

                          {(user?.role === 'admin' || user?.unitType === 'accounting' || user?.role === 'branch' || user?.id === contract.created_by) && (
                            <button
                              onClick={() => handleDeleteContract(contract.id)}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Sil
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showUploadModal && selectedContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Sözleşme Dosyası Yükle
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedContract(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Sözleşme No: <span className="font-semibold">{selectedContract.contract_number}</span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Öğrenci: <span className="font-semibold">
                  {selectedContract.students?.first_name} {selectedContract.students?.last_name}
                </span>
              </p>
            </div>

            <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <label className="cursor-pointer">
                <span className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                  Dosya seçin
                </span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileUpload(selectedContract.id, file);
                    }
                  }}
                  disabled={uploadingFile}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                PDF, DOC veya DOCX (Maks. 10MB)
              </p>
              {uploadingFile && (
                <div className="mt-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Yükleniyor...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showFixStudentModal && contractToFix && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-800 hover:border-blue-500/50 transition-colors">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Öğrenci Bilgisini Düzelt
              </h3>
              <button
                onClick={() => {
                  setShowFixStudentModal(false);
                  setContractToFix(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Bu sözleşmeye <strong>({contractToFix.contract_number})</strong> bağlı öğrenci kaydı bulanamadı. Lütfen öğrencinin bilgilerini girin. Yeni bir öğrenci kaydı oluşturulup bu sözleşmeye otomatik bağlanacaktır.
            </p>
            <form onSubmit={handleFixStudent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ad *</label>
                <input
                  type="text"
                  required
                  value={fixStudentForm.first_name}
                  onChange={(e) => setFixStudentForm({ ...fixStudentForm, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn: Ahmet"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Soyad *</label>
                <input
                  type="text"
                  required
                  value={fixStudentForm.last_name}
                  onChange={(e) => setFixStudentForm({ ...fixStudentForm, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn: Yılmaz"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon (Opsiyonel)</label>
                <input
                  type="text"
                  value={fixStudentForm.phone}
                  onChange={(e) => setFixStudentForm({ ...fixStudentForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn: 0555 555 55 55"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-posta (Opsiyonel)</label>
                <input
                  type="email"
                  value={fixStudentForm.email}
                  onChange={(e) => setFixStudentForm({ ...fixStudentForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn: ahmet@email.com"
                />
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="submit"
                  disabled={fixingStudent}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {fixingStudent ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowFixStudentModal(false);
                    setContractToFix(null);
                  }}
                  disabled={fixingStudent}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 font-medium"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailModal && selectedContract && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-800">
            <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-white">
                  Sözleşme Detayları
                </h3>
                <p className="text-blue-100 text-sm">
                  {selectedContract.contract_number}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedContract(null);
                }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Öğrenci</p>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedContract.students?.first_name} {selectedContract.students?.last_name}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Şube</p>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedContract.branches?.name || 'Belirtilmemiş'}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Sözleşme Tarihi</p>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {format(new Date(selectedContract.contract_date), 'dd MMMM yyyy', { locale: tr })}
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      {getCurrencyIcon(selectedContract.currency, "w-4 h-4 text-green-600 dark:text-green-400")}
                    </div>
                    <p className="text-xs text-green-700 dark:text-green-400 uppercase tracking-wider font-semibold">Toplam Tutar</p>
                  </div>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-300">
                    {selectedContract.currency} {selectedContract.total_amount.toLocaleString()}
                  </p>
                </div>

                {selectedContract.creator && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      <p className="text-xs text-purple-700 dark:text-purple-400 uppercase tracking-wider font-semibold">Olusturan</p>
                    </div>
                    <p className="text-lg font-semibold text-purple-900 dark:text-purple-300">
                      {selectedContract.creator.full_name}
                    </p>
                  </div>
                )}

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Oluşturulma Tarihi</p>
                  </div>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {format(new Date(selectedContract.created_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                  </p>
                </div>
              </div>

              {selectedContract.lead_info?.interested_programs && selectedContract.lead_info.interested_programs.length > 0 && (
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4 border border-teal-200 dark:border-teal-800">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                    <p className="text-xs text-teal-700 dark:text-teal-400 uppercase tracking-wider font-semibold">Program/Universite</p>
                  </div>
                  <p className="text-base font-semibold text-teal-900 dark:text-teal-300">
                    {selectedContract.lead_info.interested_programs.join(', ')}
                  </p>
                </div>
              )}

              {selectedContract.lead_info?.price_notes && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <p className="text-xs text-amber-700 dark:text-amber-400 uppercase tracking-wider font-semibold">Ucret Ek Bilgi</p>
                  </div>
                  <p className="text-base text-amber-900 dark:text-amber-300">
                    {selectedContract.lead_info.price_notes}
                  </p>
                </div>
              )}

              {selectedContract.notes && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Notlar</p>
                  </div>
                  <p className="text-base text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {selectedContract.notes}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                {getStatusBadge(selectedContract.status)}
                {selectedContract.transferred_to_operations && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <CheckCircle className="w-4 h-4" />
                    Operasyona Gönderildi
                  </div>
                )}
              </div>

              {selectedContract.contract_file_url && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <a
                    href={selectedContract.contract_file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Sözleşme Dosyasını İndir
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AddContractModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={fetchContracts}
      />
    </div>
  );
};

export default Contracts;
